import { SPEC_TYPE_LABEL, type SpecType } from "@/api/contracts/specifications";

// Every string the Spec list's lifecycle verbs print (#696), in one table the
// dialogs, the host and their tests all read — so a test asserting a title and
// the dialog drawing it cannot disagree, and the parity review has one place to
// hold against the source.
//
// Verbatim from the donor, typo and casing included:
//
//   - create: `SpecCreateIsland/components/create-spec-dialog.tsx:20-45`, whose
//     base-new subtitle says "can be use";
//   - rename: `spec-actions-button/edit-spec-dialog.tsx` (core and base) and the
//     community island's EDIT variant (`CommunitySpecCreateEditDupIsland/
//     components/community-spec-create-edit-delete-dialog.tsx`);
//   - archive and enable: `spec-actions-button/archive-spec-dialog/actions.ts`
//     and `notifications.tsx`, a lowercase verb in the topic;
//   - delete: `spec-actions-button/delete-spec-dialog/delete-spec-dialog.tsx`
//     and `actions.ts`, where the topic's "Deleted" is capitalised;
//   - the Division select: `DivisionInputIsland/division-input-island.tsx`.

type Verb = "archived" | "enabled";

/** `${Core|Base|Community} Specification ${verb}` — the snackbar's bold line. */
export function specTopic(specType: SpecType, verb: Verb | "Deleted"): string {
  return `${SPEC_TYPE_LABEL[specType]} Specification ${verb}`;
}

/** What a delete names: `<name> (v.<code>)`, or `<name> (Draft)` with no code. */
export function deleteCaption(name: string, versionCode: string | null): string {
  return versionCode ? `${name} (v.${versionCode})` : `${name} (Draft)`;
}

export const SPEC_LIFECYCLE_COPY = {
  nameLabel: "Specification Name",
  requiredField: "Required field",
  cancel: "Cancel",

  division: {
    label: "Division",
    accessibleName: "Division dropdown",
    loadError: "Error loading divisions, please refresh page.",
  },

  create: {
    core: {
      title: "Create Core Specification",
      subtitle:
        "Create a new Core Specification that can be used across different Base Specifications.",
    },
    baseNew: {
      title: "Create Base Specification",
      subtitle: "Create a new Base Specification that can be use across different communities.",
    },
    error: "Error creating Specification. Please try again.",
  },

  rename: {
    plain: {
      title: "Edit Specification Name",
      subtitle:
        "Edit Specification name. Changes will be reflected for all Versions of the Specification.",
      error: "Error updating specification. Please try again.",
    },
    community: {
      title: "Edit Specification Name & Community ID",
      subtitle:
        "Edit Specification name or Community ID. Changes will be reflected for all Versions of the Specification.",
      error: "Error updating Community Specification. Please try again.",
    },
    basedOn: "Based on:",
    save: { label: "Save", pending: "Saving", ariaLabel: "Save Edits" },
  },

  /**
   * The Editor header pencil's extra copy (#806): `SpecificationFormDialog`'s
   * `edit` title and button, and the notes field both dialogs add
   * (`SpecificationFormDialog.tsx:25-116`, the community island's
   * `edit-with-notes`). The community dialog's own strings are `rename`'s.
   */
  header: {
    plainTitle: "Edit",
    save: "Save",
    notesLabel: "Notes",
    notesPlaceholder: "Add notes",
  },

  /**
   * The version cloner's dialogs (#812), each the donor's own:
   *
   *   - `version`: `components/specifications/shared/CreateVersionDialog.tsx`
   *     — a single-quoted string continued over four lines, so each sentence
   *     break keeps the next line's four-space indent, and a stray closing
   *     quote — and `spec-edit-button/create-spec-version-dialog.tsx`'s error;
   *   - `duplicate`: `spec-actions-button/duplicate-spec-dialog/
   *     duplicate-spec-dialog.tsx:28-41`, the core subtitle without its period;
   *   - `baseFromCore`: `SpecCreateIsland/components/create-spec-dialog.tsx`'s
   *     `base` copy, whose error is `create.error`;
   *   - `community`: the community island's `duplicate`, `create` and
   *     `create-from-community` variants (`community-spec-create-edit-delete-
   *     dialog.tsx:30-74`), and its `BaseSpecPicker` (`:80-150`).
   */
  cloner: {
    version: {
      title: "Editing Specification",
      body:
        "The current version of this Specification is published.     By continuing to the edit mode, you are opening a new draft.     " +
        'Drafts will remain "In Progress" until published.     Draft specifications are not visible to external or logged out users."',
      cancel: { label: "Cancel", ariaLabel: "Cancel creating a new version." },
      submit: { label: "Continue", pending: "Creating", ariaLabel: "Create a new version." },
      error: "Error creating a new draft specification version. Please try again.",
    },
    duplicate: {
      core: {
        title: "Duplicate Core Specification",
        subtitle:
          "Create a copy of a Core Specification to use as a new separate Core Specification",
      },
      base: {
        title: "Duplicate Base Specification",
        subtitle:
          "Create a copy of a Base Specification to use as a new separate Base Specification.",
      },
      nameLabel: "Name",
      submit: { label: "Duplicate", pending: "Duplicating", ariaLabel: "Duplicate" },
      error: "Error duplicating specification. Please try again.",
    },
    /** The copy's name: the source's `` `${specName} - Copy` ``. */
    copyName: (name: string) => `${name} - Copy`,
    baseFromCore: {
      title: "Create Base Specification",
      subtitle: "Base Specifications are created from a Core Specification.",
      nameLabel: "Base Name",
      basedOn: "Based on:",
    },
    community: {
      duplicate: {
        title: "Duplicate Community Specification",
        subtitle:
          "Duplicated Community Specification remains linked to original Base Specification.",
        submit: { label: "Duplicate", pending: "Duplicating", ariaLabel: "Duplicate" },
        error: "Error duplicating Community Specification. Please try again.",
      },
      create: {
        title: "Create Community Specification",
        subtitle:
          "Community Specifications are created from a Base Specification and used for a Community.",
        submit: { label: "Create Specification", pending: "Creating", ariaLabel: "Create" },
        error: "Error creating Community Specification. Please try again.",
      },
      createFromCommunity: {
        title: "Create Community Specification",
        subtitle: "Select a Base Specification and name the new Community Specification.",
        community: "Community:",
        submit: { label: "Create Specification", pending: "Creating", ariaLabel: "Create" },
        error: "Error creating Community Specification. Please try again.",
      },
      basePicker: {
        label: "Base Specification",
        /** `` `${option.name} (v.${versionCode ?? '?'})` ``. */
        option: (name: string, versionCode: string | null) => `${name} (v.${versionCode ?? "?"})`,
      },
    },
  },

  archive: {
    /** The success snackbar's message; `archived` false is Enable. */
    message: (name: string, archived: boolean) =>
      `${name} has been successfully ${archived ? "archived" : "enabled"}.`,
    error: (archived: boolean) =>
      `Error ${archived ? "archiving" : "enabling"} specification. Please try again.`,
  },

  delete: {
    title: (specType: SpecType, versionCode: string | null) =>
      `Delete ${SPEC_TYPE_LABEL[specType]} Specification ${versionCode ? "Version" : "Draft"}`,
    question: "Are you sure that you want to delete?",
    warning: "This action cannot be undone.",
    confirm: { label: "Delete", pending: "Deleting" },
    topic: (specType: SpecType) => specTopic(specType, "Deleted"),
    message: (caption: string) => `${caption} has been successfully deleted.`,
    error: (specType: SpecType) =>
      `Error deleting ${SPEC_TYPE_LABEL[specType]} Specification. Please try again.`,
  },
} as const;
